import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MuiButton from '@mui/material/Button';
import { APP_NAME, SUPPORT_EMAIL } from '@/config/appConfig';
import Button from '@components/common/atoms/AppButton';
import Icon from '@components/common/atoms/Icon';
import SectionEyebrow from './SectionEyebrow';

const LandingContact: React.FC = () => {
  const [contact, setContact] = useState({ name: '', company: '', email: '', message: '' });
  const [sent, setSent] = useState(false);

  const handleContactSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Subscription enquiry — ${contact.company || contact.name || APP_NAME}`);
    const body = encodeURIComponent(
      `Name: ${contact.name}\nCompany: ${contact.company}\nWork email: ${contact.email}\n\n${contact.message}`
    );
    // No public backend endpoint for enquiries — hand off to the user's mail client.
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
    setSent(true);
  };

  return (
    <Box component="section" id="contact" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
      <Box className="overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-xl dark:border-slate-800/80 dark:bg-slate-900">
        <Box className="grid lg:grid-cols-2">
          {/* Left — pitch panel */}
          <Box className="relative overflow-hidden bg-gradient-to-br from-indigo-950 via-purple-900 to-violet-950 p-8 text-white sm:p-12">
            <Box className="pointer-events-none absolute inset-0 overflow-hidden">
              <Box className="animate-float absolute -bottom-10 -right-10 h-64 w-64 rounded-full bg-fuchsia-500/20 blur-3xl" />
              <Box className="absolute -top-10 left-0 h-56 w-56 rounded-full bg-indigo-500/20 blur-3xl animate-pulse" />
            </Box>
            <Box className="relative">
              <SectionEyebrow>
                <Typography component="span" className="text-amber-300">Contact us for a subscription</Typography>
              </SectionEyebrow>
              <Typography component="h2" className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">
                Ready to bring {APP_NAME} to your team?
              </Typography>
              <Typography component="p" className="mt-4 text-lg leading-relaxed text-white/80">
                Tell us a little about your organisation and our team will reach out with a
                tailored subscription and a personalised walkthrough.
              </Typography>

              <ul className="mt-8 space-y-4">
                {[
                  'Personalised onboarding & setup',
                  'Flexible plans that scale with your team',
                  'Priority support and Microsoft 365 integration',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-white/15">
                      <Icon name="Check" size={14} className="text-emerald-300" />
                    </span>
                    <Typography component="span" className="text-sm text-white/85">{item}</Typography>
                  </li>
                ))}
              </ul>

              <Box className="mt-10 space-y-3 border-t border-white/15 pt-6">
                <a
                  href="mailto:HumanRelations@parallel-minds.com"
                  className="flex items-center gap-3 text-sm text-white/85 transition-colors hover:text-white"
                >
                  <Icon name="Mail" size={18} className="text-indigo-300" />
                  HumanRelations@parallel-minds.com
                </a>
                <div className="flex items-center gap-3 text-sm text-white/85">
                  <Icon name="Globe" size={18} className="text-indigo-300" />
                  {APP_NAME} · Human Resource Management System
                </div>
              </Box>
            </Box>
          </Box>

          {/* Right — enquiry form */}
          <Box className="p-8 sm:p-12">
            {sent ? (
              <Box className="flex h-full flex-col items-center justify-center py-10 text-center">
                <Box className="flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-100 dark:bg-emerald-950/40">
                  <Icon name="CheckCircle" size={32} className="text-emerald-600 dark:text-emerald-400" />
                </Box>
                <Typography component="h3" className="mt-6 text-xl font-bold">Thanks for reaching out!</Typography>
                <Typography component="p" className="mt-2 max-w-sm text-sm text-slate-600 dark:text-slate-400">
                  Your email client should have opened with your enquiry. If it didn't, write to
                  us directly at{' '}
                  <a href="mailto:HumanRelations@parallel-minds.com" className="font-semibold text-indigo-600 dark:text-indigo-400">
                    HumanRelations@parallel-minds.com
                  </a>
                  .
                </Typography>
                <MuiButton
                  onClick={() => setSent(false)}
                  className="mt-6 text-sm font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                >
                  Send another enquiry
                </MuiButton>
              </Box>
            ) : (
              <form onSubmit={handleContactSubmit} className="space-y-5">
                <Box>
                  <Typography component="h3" className="text-xl font-bold">Request a subscription</Typography>
                  <Typography component="p" className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    We'll get back to you within one business day.
                  </Typography>
                </Box>

                <Box className="grid gap-4 sm:grid-cols-2">
                  <Box>
                    <label htmlFor="lp-name" className="app-label mb-1.5 block text-slate-700 dark:text-slate-300">
                      Full name
                    </label>
                    <input
                      id="lp-name"
                      type="text"
                      required
                      value={contact.name}
                      onChange={(e) => setContact({ ...contact, name: e.target.value })}
                      placeholder="Jane Doe"
                      className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                    />
                  </Box>
                  <Box>
                    <label htmlFor="lp-company" className="app-label mb-1.5 block text-slate-700 dark:text-slate-300">
                      Company
                    </label>
                    <input
                      id="lp-company"
                      type="text"
                      value={contact.company}
                      onChange={(e) => setContact({ ...contact, company: e.target.value })}
                      placeholder="Acme Inc."
                      className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                    />
                  </Box>
                </Box>

                <Box>
                  <label htmlFor="lp-email" className="app-label mb-1.5 block text-slate-700 dark:text-slate-300">
                    Work email
                  </label>
                  <input
                    id="lp-email"
                    type="email"
                    required
                    value={contact.email}
                    onChange={(e) => setContact({ ...contact, email: e.target.value })}
                    placeholder="you@company.com"
                    className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                </Box>

                <Box>
                  <label htmlFor="lp-message" className="app-label mb-1.5 block text-slate-700 dark:text-slate-300">
                    How can we help?
                  </label>
                  <textarea
                    id="lp-message"
                    rows={4}
                    value={contact.message}
                    onChange={(e) => setContact({ ...contact, message: e.target.value })}
                    placeholder="Tell us about your team size and what you're looking for…"
                    className="w-full resize-none rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                </Box>

                <Button type="submit" variant="primary" size="lg" className="w-full">
                  <Icon name="Send" size={18} className="mr-2" />
                  Send enquiry
                </Button>
                <Typography component="p" className="text-center text-xs text-slate-400 dark:text-slate-500">
                  By submitting, you agree to be contacted about {APP_NAME}.
                </Typography>
              </form>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LandingContact;
